import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, CircleCheck, LoaderCircle, Minus, Plus } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const COUNTRY_CODES = [
  ['+355', 'AL'], ['+383', 'XK'], ['+389', 'MK'], ['+39', 'IT'], ['+49', 'DE'], ['+44', 'GB'],
  ['+33', 'FR'], ['+34', 'ES'], ['+30', 'GR'], ['+41', 'CH'], ['+43', 'AT'], ['+31', 'NL'],
  ['+32', 'BE'], ['+90', 'TR'], ['+1', 'US/CA'],
];

const MAX_PASSENGERS = 8;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const pad = (n) => String(n).padStart(2, '0');
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const emptyDetails = () => ({
  date: todayISO(),
  time: '',
  passengers: 1,
  name: '',
  countryCode: '+355',
  phone: '',
  email: '',
});

function FieldError({ id, message }) {
  if (!message) return null;
  return (
    <p id={id} className="mb-0 mt-1.5 text-sm font-medium text-red-700">
      {message}
    </p>
  );
}

// Three-step booking card: route, time, contact details. `direction` and the
// pickup/drop-off fields are owned by the hero so the big fare beside the card
// follows the route toggle.
function BookingWizard({ direction, onDirectionChange, places, onPlacesChange }) {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [details, setDetails] = useState(emptyDetails);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [done, setDone] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const headingRefs = useRef([]);
  const shownStep = useRef(0);

  const steps = [t('site.wizard.steps.route'), t('site.wizard.steps.when'), t('site.wizard.steps.details')];

  useEffect(() => {
    // Only react to real step changes (not the first render, and not React's
    // development-mode double effect run).
    if (shownStep.current === step) return;
    shownStep.current = step;
    if (!done) headingRefs.current[step]?.focus({ preventScroll: true });
    setAnnouncement(t('site.wizard.stepOf', { current: step + 1, total: steps.length, title: steps[step] }));
    // steps is rebuilt each render from t(); step is the real trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const set = (field) => (e) => {
    const value = e.target.value;
    setDetails((d) => ({ ...d, [field]: value }));
    if (errors[field]) setErrors((errs) => ({ ...errs, [field]: undefined }));
  };

  const setPlace = (field) => (e) => {
    onPlacesChange({ ...places, [field]: e.target.value });
    if (errors[field]) setErrors((errs) => ({ ...errs, [field]: undefined }));
  };

  const validate = (index) => {
    const next = {};
    const required = t('site.wizard.errors.required');
    if (index === 0) {
      if (!places.pickup.trim()) next.pickup = required;
      if (!places.dropoff.trim()) next.dropoff = required;
    }
    if (index === 1) {
      if (!details.date) next.date = required;
      if (!details.time) next.time = required;
      if (details.date && details.time && new Date(`${details.date}T${details.time}`) < new Date(Date.now() - 60000)) {
        next.time = t('site.wizard.errors.past');
      }
    }
    if (index === 2) {
      if (!details.name.trim()) next.name = required;
      const digits = details.phone.replace(/\D/g, '');
      if (!digits) next.phone = required;
      else if (digits.length < 6 || digits.length > 14) next.phone = t('site.wizard.errors.phone');
      if (!details.email.trim()) next.email = required;
      else if (!EMAIL_PATTERN.test(details.email.trim())) next.email = t('site.wizard.errors.email');
    }
    setErrors(next);
    const firstInvalid = Object.keys(next)[0];
    if (firstInvalid) {
      setAnnouncement(t('site.wizard.errors.summary', { count: Object.keys(next).length }));
      document.getElementById(`wizard-${firstInvalid}`)?.focus();
      return false;
    }
    return true;
  };

  const goNext = () => {
    if (validate(step)) setStep((s) => Math.min(s + 1, steps.length - 1));
  };

  const goBack = () => {
    setErrors({});
    setStep((s) => Math.max(s - 1, 0));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (step < steps.length - 1) {
      goNext();
      return;
    }
    if (!validate(2)) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      await axios.post(`${API_URL}/api/bookings`, {
        customer_name: details.name.trim(),
        customer_phone: `${details.countryCode} ${details.phone.trim()}`,
        customer_email: details.email.trim(),
        pickup_location: places.pickup.trim(),
        destination: places.dropoff.trim(),
        pickup_datetime: `${details.date}T${details.time}`,
        notes: t('site.wizard.passengersNote', { count: details.passengers }),
      });
      setDone(true);
      setAnnouncement(t('site.wizard.success.title'));
    } catch (error) {
      const message =
        error.response?.status === 429 ? t('site.wizard.errors.tooMany') : t('site.wizard.errors.submit');
      setSubmitError(message);
      setAnnouncement(message);
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setDetails(emptyDetails());
    setErrors({});
    setStep(0);
    setDone(false);
  };

  const fieldProps = (field) => ({
    id: `wizard-${field}`,
    'aria-invalid': errors[field] ? true : undefined,
    'aria-describedby': errors[field] ? `wizard-${field}-error` : undefined,
  });
  const fieldClass = (field) => `field ${errors[field] ? 'field-invalid' : ''}`;
  const label = 'mb-1.5 block text-sm font-semibold text-navy';

  const routeOptions = [
    { value: 'toCity', label: t('site.wizard.toCity') },
    { value: 'toAirport', label: t('site.wizard.toAirport') },
  ];

  const onRouteKey = (e) => {
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) return;
    e.preventDefault();
    const next = direction === 'toCity' ? 'toAirport' : 'toCity';
    onDirectionChange(next);
    document.getElementById(`route-${next}`)?.focus();
  };

  if (done) {
    return (
      <div className="rounded-card bg-white p-6 text-navy shadow-card sm:p-7">
        <div role="status" aria-live="polite" className="sr-only">{announcement}</div>
        <CircleCheck size={48} aria-hidden="true" className="text-emerald-600" />
        <h2 className="m-0 mt-4 text-2xl font-bold tracking-tight">{t('site.wizard.success.title')}</h2>
        <p className="mb-0 mt-3 leading-relaxed text-slate-600">{t('site.wizard.success.text')}</p>
        <button type="button" className="btn-outline-navy mt-6 w-full" onClick={reset}>
          {t('site.wizard.success.again')}
        </button>
      </div>
    );
  }

  return (
    <form noValidate onSubmit={handleSubmit} className="overflow-hidden rounded-card bg-white text-navy shadow-card">
      <div role="status" aria-live="polite" className="sr-only">{announcement}</div>

      <div className="px-5 pt-5 sm:px-7 sm:pt-6">
        <p className="m-0 text-sm font-semibold text-slate-600">
          {t('site.wizard.stepOf', { current: step + 1, total: steps.length, title: steps[step] })}
        </p>
        <ol className="m-0 mt-3 grid list-none grid-cols-3 gap-2 p-0" aria-hidden="true">
          {steps.map((name, i) => (
            <li key={name} className={`h-1.5 rounded-full transition-colors duration-300 ${i <= step ? 'bg-taxi' : 'bg-slate-200'}`} />
          ))}
        </ol>
      </div>

      <div className="relative overflow-hidden">
        <div
          className="flex w-[300%] transition-transform duration-500 ease-out motion-reduce:transition-none"
          style={{ transform: `translateX(-${(step * 100) / 3}%)` }}
        >
          {/* Step 1: route */}
          <fieldset
            className="m-0 w-1/3 min-w-0 border-0 px-5 pb-1 pt-4 sm:px-7"
            {...(step === 0 ? {} : { inert: '', 'aria-hidden': true })}
          >
            <legend className="sr-only">{steps[0]}</legend>
            <h2 ref={(el) => (headingRefs.current[0] = el)} tabIndex={-1} className="sr-only">
              {steps[0]}
            </h2>
            <div
              role="radiogroup"
              aria-label={t('site.wizard.routeLabel')}
              className="grid grid-cols-2 gap-1 rounded-full bg-mist p-1"
            >
              {routeOptions.map((option) => {
                const checked = direction === option.value;
                return (
                  <button
                    key={option.value}
                    id={`route-${option.value}`}
                    type="button"
                    role="radio"
                    aria-checked={checked}
                    tabIndex={checked ? 0 : -1}
                    onKeyDown={onRouteKey}
                    onClick={() => onDirectionChange(option.value)}
                    className={`focus-ring-light min-h-[44px] cursor-pointer rounded-full border-0 px-2 text-[13px] font-bold leading-tight transition-colors duration-200 sm:text-sm ${
                      checked ? 'bg-navy text-white shadow' : 'bg-transparent text-slate-600 hover:text-navy'
                    }`}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
            <div className="mt-3 space-y-3">
              <div>
                <label htmlFor="wizard-pickup" className={label}>{t('site.wizard.pickup')}</label>
                <input
                  {...fieldProps('pickup')}
                  type="text"
                  autoComplete="off"
                  value={places.pickup}
                  onChange={setPlace('pickup')}
                  placeholder={t('site.wizard.addressPlaceholder')}
                  className={fieldClass('pickup')}
                />
                <FieldError id="wizard-pickup-error" message={errors.pickup} />
              </div>
              <div>
                <label htmlFor="wizard-dropoff" className={label}>{t('site.wizard.dropoff')}</label>
                <input
                  {...fieldProps('dropoff')}
                  type="text"
                  autoComplete="off"
                  value={places.dropoff}
                  onChange={setPlace('dropoff')}
                  placeholder={t('site.wizard.addressPlaceholder')}
                  className={fieldClass('dropoff')}
                />
                <FieldError id="wizard-dropoff-error" message={errors.dropoff} />
              </div>
            </div>
          </fieldset>

          {/* Step 2: when */}
          <fieldset
            className="m-0 w-1/3 min-w-0 border-0 px-5 pb-1 pt-4 sm:px-7"
            {...(step === 1 ? {} : { inert: '', 'aria-hidden': true })}
          >
            <legend className="sr-only">{steps[1]}</legend>
            <h2 ref={(el) => (headingRefs.current[1] = el)} tabIndex={-1} className="sr-only">
              {steps[1]}
            </h2>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="wizard-date" className={label}>{t('site.wizard.date')}</label>
                <input
                  {...fieldProps('date')}
                  type="date"
                  min={todayISO()}
                  value={details.date}
                  onChange={set('date')}
                  className={fieldClass('date')}
                />
                <FieldError id="wizard-date-error" message={errors.date} />
              </div>
              <div>
                <label htmlFor="wizard-time" className={label}>{t('site.wizard.time')}</label>
                <input
                  {...fieldProps('time')}
                  type="time"
                  value={details.time}
                  onChange={set('time')}
                  className={fieldClass('time')}
                />
                <FieldError id="wizard-time-error" message={errors.time} />
              </div>
            </div>
            <div className="mt-3">
              <span id="wizard-passengers-label" className={label}>{t('site.wizard.passengers')}</span>
              <div className="flex items-center gap-3" role="group" aria-labelledby="wizard-passengers-label">
                <button
                  type="button"
                  aria-label={t('site.wizard.fewer')}
                  disabled={details.passengers <= 1}
                  onClick={() => setDetails((d) => ({ ...d, passengers: d.passengers - 1 }))}
                  className="focus-ring-light inline-flex h-11 w-11 cursor-pointer items-center justify-center rounded-full border-2 border-solid border-slate-300 bg-white text-navy transition-colors hover:border-navy disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Minus size={18} aria-hidden="true" />
                </button>
                <output aria-live="polite" className="min-w-[2ch] text-center text-xl font-bold">
                  {details.passengers}
                </output>
                <button
                  type="button"
                  aria-label={t('site.wizard.more')}
                  disabled={details.passengers >= MAX_PASSENGERS}
                  onClick={() => setDetails((d) => ({ ...d, passengers: d.passengers + 1 }))}
                  className="focus-ring-light inline-flex h-11 w-11 cursor-pointer items-center justify-center rounded-full border-2 border-solid border-slate-300 bg-white text-navy transition-colors hover:border-navy disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Plus size={18} aria-hidden="true" />
                </button>
              </div>
            </div>
          </fieldset>

          {/* Step 3: details */}
          <fieldset
            className="m-0 w-1/3 min-w-0 border-0 px-5 pb-1 pt-4 sm:px-7"
            {...(step === 2 ? {} : { inert: '', 'aria-hidden': true })}
          >
            <legend className="sr-only">{steps[2]}</legend>
            <h2 ref={(el) => (headingRefs.current[2] = el)} tabIndex={-1} className="sr-only">
              {steps[2]}
            </h2>
            <div className="mt-3 space-y-3">
              <div>
                <label htmlFor="wizard-name" className={label}>{t('site.wizard.name')}</label>
                <input
                  {...fieldProps('name')}
                  type="text"
                  autoComplete="name"
                  value={details.name}
                  onChange={set('name')}
                  className={fieldClass('name')}
                />
                <FieldError id="wizard-name-error" message={errors.name} />
              </div>
              <div>
                <label htmlFor="wizard-phone" className={label}>{t('site.wizard.phone')}</label>
                <div className="flex gap-2">
                  <select
                    aria-label={t('site.wizard.countryCode')}
                    value={details.countryCode}
                    onChange={set('countryCode')}
                    className="field w-[6.5rem] shrink-0 cursor-pointer px-2"
                  >
                    {COUNTRY_CODES.map(([code, country]) => (
                      <option key={code} value={code}>{`${code} ${country}`}</option>
                    ))}
                  </select>
                  <input
                    {...fieldProps('phone')}
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel-national"
                    value={details.phone}
                    onChange={set('phone')}
                    placeholder="68 123 4567"
                    className={`${fieldClass('phone')} min-w-0`}
                  />
                </div>
                <FieldError id="wizard-phone-error" message={errors.phone} />
              </div>
              <div>
                <label htmlFor="wizard-email" className={label}>{t('site.wizard.email')}</label>
                <input
                  {...fieldProps('email')}
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  value={details.email}
                  onChange={set('email')}
                  placeholder={t('site.wizard.emailPlaceholder')}
                  className={fieldClass('email')}
                />
                <FieldError id="wizard-email-error" message={errors.email} />
              </div>
            </div>
          </fieldset>
        </div>
      </div>

      <div className="px-5 pb-5 pt-3 sm:px-7 sm:pb-6">
        {submitError && (
          <p role="alert" className="mb-4 mt-0 rounded-field bg-red-50 px-4 py-3 text-sm font-medium text-red-800">
            {submitError}
          </p>
        )}
        <div className="flex gap-3">
          {step > 0 && (
            <button type="button" className="btn-outline-navy px-4" onClick={goBack}>
              <ArrowLeft size={18} aria-hidden="true" />
              {t('site.wizard.back')}
            </button>
          )}
          {step < steps.length - 1 ? (
            <button type="submit" className="btn-primary focus-ring-light flex-1">
              {t('site.wizard.next')}
              <ArrowRight size={18} aria-hidden="true" />
            </button>
          ) : (
            <button type="submit" className="btn-primary focus-ring-light flex-1" disabled={submitting} aria-busy={submitting}>
              {submitting && <LoaderCircle size={18} aria-hidden="true" className="animate-spin" />}
              {submitting ? t('site.wizard.submitting') : t('site.nav.bookNow')}
            </button>
          )}
        </div>
        {step === steps.length - 1 && (
          <p className="mb-0 mt-3 text-center text-xs leading-snug text-slate-600">
            <Trans
              i18nKey="site.wizard.legal"
              components={{
                terms: <Link to="/terms" className="font-semibold text-navy underline underline-offset-2" />,
                privacy: <Link to="/privacy" className="font-semibold text-navy underline underline-offset-2" />,
              }}
            />
          </p>
        )}
      </div>
    </form>
  );
}

export default BookingWizard;
